import React from 'react';
import { getAdministrator } from '@/api/accounts';
import { getUser } from '@/api/users';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { PencilSimple as PencilSimpleIcon } from '@phosphor-icons/react/dist/ssr/PencilSimple';
import { User as UserIcon } from '@phosphor-icons/react/dist/ssr/User';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';

import { EditAdministratorModal } from '../components/edit-administrator-modal';

interface AccountAdministratorApiResponse {
  administrator: User;
}

export function AccountAdministratorPage(): React.JSX.Element {
  const { administratorId } = useParams<{ administratorId: string }>();
  const { account, user } = useUser();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery<AccountAdministratorApiResponse>({
    queryKey: ['administrator', administratorId],
    queryFn: async () => {
      return await getAdministrator(account!.id, administratorId!);
    },
  });

  const administrator = data?.administrator;

  console.log('administrator', administrator);

  const handleOnSuccess = React.useCallback(
    (user: User) => {
      toast.success('User updated successfully', {
        style: {
          background: '#107E4A',
          color: '#fff',
          borderColor: '#107E4A',
        },
        description: 'The user has been successfully updated.',
        position: 'top-right',
      });
      refetch();
    },
    [refetch]
  );

  const dialog = useDialog();
  return (
    <React.Fragment>
      <Box sx={{ mb: 2 }}>
        <Button
          aria-label="Back to Administrators"
          onClick={() => {
            navigate(paths.settings.account.administrators.index);
          }}
          startIcon={<ArrowLeftIcon size={24} />}
          variant="text"
        >
          Back to Administrators
        </Button>
      </Box>

      {isLoading ? <div>Loading...</div> : null}
      {error ? <div>Error: {error.message}</div> : null}

      <Stack spacing={4}>
        <Grid container spacing={4}>
          {data ? (
            <Grid lg={12} xs={12}>
              <Stack spacing={4}>
                <Card>
                  <CardHeader
                    action={
                      <IconButton
                        onClick={() => {
                          dialog.handleOpen();
                        }}
                      >
                        <PencilSimpleIcon />
                      </IconButton>
                    }
                    avatar={
                      <Avatar>
                        <UserIcon fontSize="var(--Icon-fontSize)" />
                      </Avatar>
                    }
                    title="User Details"
                  />
                  <PropertyList
                    divider={<Divider />}
                    orientation="vertical"
                    sx={{ '--PropertyItem-padding': '12px 24px' }}
                  >
                    {(
                      [
                        { key: 'ID', value: <Chip label={data.administrator.id} size="small" variant="soft" /> },
                        { key: 'Name', value: `${data.administrator.first_name} ${data.administrator.last_name}` },
                        { key: 'Email', value: data.administrator.email },
                        { key: 'Phone', value: data.administrator.phone },
                      ] satisfies { key: string; value: React.ReactNode }[]
                    ).map(
                      (item): React.JSX.Element => (
                        <PropertyItem key={item.key} name={item.key} value={item.value} />
                      )
                    )}
                  </PropertyList>
                </Card>
              </Stack>
            </Grid>
          ) : null}
        </Grid>
      </Stack>
      <EditAdministratorModal
        accountId={account?.id!}
        onClose={dialog.handleClose}
        onSuccess={handleOnSuccess}
        open={dialog.open}
        user={administrator ?? ({} as User)}
      />
    </React.Fragment>
  );
}
